/**
 * Health Authority Badge Component
 * Displays health authority source with credibility indicator
 */

import React from "react";
import { Shield, ExternalLink } from "lucide-react";
import type { HealthAuthority } from "@/services/api/healthRecommendations";

interface HealthAuthorityBadgeProps {
  authority: HealthAuthority;
  showUrl?: boolean;
}

export const HealthAuthorityBadge: React.FC<HealthAuthorityBadgeProps> = ({
  authority,
  showUrl = false,
}) => {
  const getCredibilityColor = (score: number) => {
    if (score >= 9) return "bg-green-100 text-green-800 border-green-300";
    if (score >= 7) return "bg-primary-100 text-primary-800 border-primary-300";
    if (score >= 5) return "bg-yellow-100 text-yellow-800 border-yellow-300";
    return "bg-gray-100 text-gray-800 border-gray-300";
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case "primary":
        return (
          <span className="ml-2 px-1.5 py-0.5 bg-teal-100 text-teal-700 text-xs rounded">
            Primary
          </span>
        );
      case "secondary":
        return (
          <span className="ml-2 px-1.5 py-0.5 bg-primary-100 text-primary-700 text-xs rounded">
            Secondary
          </span>
        );
      default:
        return null;
    }
  };

  const getCountryFlag = (countryCode: string) => {
    // Simple flag emoji mapping (extend as needed)
    const flags: Record<string, string> = {
      US: "🇺🇸",
      DE: "🇩🇪",
      GB: "🇬🇧",
      FR: "🇫🇷",
      ES: "🇪🇸",
      IT: "🇮🇹",
      CH: "🇨🇭",
      AT: "🇦🇹",
      CA: "🇨🇦",
      AU: "🇦🇺",
      BR: "🇧🇷",
      MX: "🇲🇽",
      INT: "🌍",
    };
    return flags[countryCode] || "🌐";
  };

  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border ${getCredibilityColor(authority.credibilityScore)}`}
    >
      <span className="text-lg">{getCountryFlag(authority.country)}</span>

      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1">
          <Shield className="h-3 w-3" />
          <span className="font-medium text-sm">{authority.name}</span>
        </div>

        {getPriorityBadge(authority.priority)}

        <div className="flex items-center gap-1 text-xs">
          <span className="opacity-60">Score:</span>
          <span className="font-semibold">{authority.credibilityScore}/10</span>
        </div>
      </div>

      {showUrl && authority.url && (
        <a
          href={authority.url}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-2 hover:opacity-70 transition-opacity"
        >
          <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </div>
  );
};
